import React, { useEffect } from "react";
import { X } from "lucide-react";

/**
 * Opsora — DrillDownPanel
 *
 * Right slide-in panel that turns any number/tile/segment into a deeper view.
 * Used across every screen — never embed bespoke modals when a drill-down
 * is needed; use this component.
 *
 * - Backdrop click closes
 * - ESC closes
 * - Only one panel mounts at a time (controlled by parent)
 */
export default function DrillDownPanel({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  width = 560,
  testId,
}) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="drilldown-backdrop"
        data-testid={testId ? `${testId}-backdrop` : "drilldown-backdrop"}
      />
      {/* Panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="drilldown-panel"
        style={{ width: `min(${width}px, 92vw)` }}
        data-testid={testId || "drilldown-panel"}
      >
        <header className="drilldown-header">
          <div>
            <h2 className="font-display text-xl text-primary" style={{ margin: 0 }}>
              {title}
            </h2>
            {subtitle && (
              <p className="font-body text-sm text-muted" style={{ margin: "4px 0 0" }}>
                {subtitle}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="drilldown-close"
            aria-label="Close panel"
            data-testid="drilldown-close"
          >
            <X size={16} />
          </button>
        </header>
        <div className="drilldown-body">{children}</div>
      </aside>
    </>
  );
}
